import { useCallback, useContext, useEffect, useState } from "react"
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react"

import circleArrowIcon from "#/assets/circle-arrow.svg"
import noLedger from "#/assets/no-ledger.svg"
import { FlowContext } from "#/shared/context"
import { usePosthogOnboardingEvent } from "#/shared/hooks"

import ConnectWalletStatus from "./ConnectWalletStatus"

type ConnectWalletButtonProps = {
  label: string
  icon?: string | (() => Promise<string>)
  variant?: "call-to-action" | "coming-soon"
  connectionRejected?: boolean
  shouldShowStatus?: boolean
  onClick?: () => void
  analyticsId?: string
}

export default function ConnectWalletButton({
  label,
  icon,
  variant = "call-to-action",
  connectionRejected = false,
  shouldShowStatus = false,
  onClick,
  analyticsId,
}: ConnectWalletButtonProps) {
  const [walletIcon, setWalletIcon] = useState<string>()

  const { isBitcoinFlow } = useContext(FlowContext)
  const { emitOnboardingButtonClickedEvent } = usePosthogOnboardingEvent()

  const onConnection = useCallback(() => {
    if (onClick) {
      onClick()

      if (analyticsId) {
        emitOnboardingButtonClickedEvent(analyticsId)
      }
    }
  }, [onClick, emitOnboardingButtonClickedEvent, analyticsId])

  useEffect(() => {
    const getWalletIcon = async () => {
      if (!icon) return

      if (typeof icon === "string") {
        setWalletIcon(icon)
      } else {
        const iconUrl = await icon()
        setWalletIcon(iconUrl)
      }
    }

    getWalletIcon()
  }, [icon])

  return (
    <Box mb={6}>
      <Button
        type="button"
        variant="unstyled"
        onClick={onConnection}
        w="100%"
        rounded={0}
        display="flex"
        justifyContent="space-between"
        p="28px 28px 28px 46px"
        bg="primary.black.5"
        cursor={variant === "call-to-action" ? "pointer" : "default"}
        _hover={{
          bg:
            variant === "call-to-action"
              ? "primary.black.10"
              : "primary.black.5",
        }}
      >
        <Flex gap={6} alignItems="center" flex={1}>
          {walletIcon && (
            <Image
              src={walletIcon}
              h="24px"
              w="24px"
              rounded="2px"
              mixBlendMode="multiply"
            />
          )}
          <Text fontWeight={300} fontSize="24px" letterSpacing="0.96px">
            {label}
          </Text>
          {label === "OKX" && isBitcoinFlow && (
            <Flex alignItems="center" gap={1} ml="auto" mr={6}>
              <Image src={noLedger} />
              <Text
                variant="label"
                fontSize="12px"
                fontWeight={400}
                fontFamily="riforma.normal"
              >
                Ledger not supported
              </Text>
            </Flex>
          )}
        </Flex>
        {variant === "call-to-action" ? (
          <Image src={circleArrowIcon} />
        ) : (
          <Box
            textTransform="uppercase"
            fontSize="14px"
            lineHeight="16px"
            p="8px 16px"
            rounded="40px"
            bg="#D0D0D0"
          >
            Coming soon
          </Box>
        )}
      </Button>
      {shouldShowStatus && (
        <ConnectWalletStatus
          onWalletConnect={onClick}
          connectionRejected={connectionRejected}
        />
      )}
    </Box>
  )
}
